import { Box } from '@material-ui/core';
import { Channel, UserType } from '@nemuru-eng/js-api-client';
import { useEffect, useRef, useState } from 'react';
import { SWRConfig } from 'swr';

import { ChannelTypeIcons } from '@/components/modules/MerchantDetail/MerchantStructure/constants/ChannelTypeIcons';
import { AddNewUser } from '@/components/modules/Users/components/AddNewUser/AddNewUser';
import { AdminCredentialsTable } from '@/components/modules/Users/components/CredentialsTable/AdminCredentialsTable';
import { AgentsCredentialsTable } from '@/components/modules/Users/components/CredentialsTable/AgentsCredentialsTable';
import { useAgentsDetails } from '@/components/modules/Users/hooks/useAgentsDetails';
import { UserCredentialsType } from '@/components/modules/Users/types/UserCredentials';
import { CustomTabs } from '@/components/shared/components/CustomTabs/CustomTabs';
import { LoadingValues } from '@/components/shared/components/LoadingValues/LoadingValues';
import { ModuleCorePermissionProps } from '@/config/types';
import { useUser } from '@/hooks/useUser';

interface ChannelCredentialsTabsProps extends ModuleCorePermissionProps {
  selectedChannel: Channel;
}

export const ChannelCredentialsTabs = ({ canEdit, selectedChannel }: ChannelCredentialsTabsProps) => {
  const { userEntity } = useUser();
  const { isAddNewAgentAllowed, loading } = useAgentsDetails({ selectedChannel });

  const [activeTab, setActiveTab] = useState(UserCredentialsType.AGENT);

  const prevChannelRef = useRef(selectedChannel);

  useEffect(() => {
    if (prevChannelRef.current.id !== selectedChannel.id) {
      setActiveTab(UserCredentialsType.AGENT);
      prevChannelRef.current = selectedChannel;
    }
  }, [selectedChannel]);

  if (loading)
    return (
      <Box p={2} height={'48px'} display={'flex'} alignItems={'center'}>
        <LoadingValues titleTextId="LOADING_AGENTS" />
      </Box>
    );

  const createNewUserAllowed = activeTab !== UserCredentialsType.AGENT ? true : isAddNewAgentAllowed();

  return (
    <SWRConfig value={{ revalidateIfStale: true }}>
      <Box mt={1} position={'relative'}>
        <CustomTabs
          tabs={[
            {
              name: UserCredentialsType.AGENT,
              panel: <AgentsCredentialsTable canEdit={canEdit} agents={selectedChannel.agents} />,
              iconName: 'person',
            },
            {
              name: UserCredentialsType.ADMIN,
              panel: <AdminCredentialsTable canEdit={canEdit} channel={selectedChannel} />,
              iconName: ChannelTypeIcons[selectedChannel.type],
            },
          ]}
        />
        {canEdit && !userEntity?.isUserType(UserType.AGENT) && (
          <Box position={'absolute'} top={'70px'} left={'0'}>
            <AddNewUser disabled={!createNewUserAllowed} type={activeTab} selectedChannel={selectedChannel} />
          </Box>
        )}
      </Box>
    </SWRConfig>
  );
};
